import { __esm, __export, __toCommonJS } from "./chunk-PNKVD2UK.js";

// src/http-clients/AuthenticationHttpClient.ts
import axios from "axios";
import { configurationProvider } from "@printdeal/configuration";
var AuthenticationHttpClient, authenticationHttpClient, AuthenticationHttpClient_default;
var init_AuthenticationHttpClient = __esm({
  "src/http-clients/AuthenticationHttpClient.ts"() {
    "use strict";

    init_authenticateHttpRequestInterceptor();
    AuthenticationHttpClient = class {
      get authenticatedClient() {
        if (!this._authenticatedClient) {
          const baseURL = configurationProvider.getValue("AUTHENTICATION_API_BASE_URL");
          this._authenticatedClient = axios.create({
            baseURL
          });
          this._authenticatedClient.interceptors.request.use(authenticateHttpRequestInterceptor);
        }
        return this._authenticatedClient;
      }
      get unauthenticatedClient() {
        if (!this._unauthenticatedClient) {
          const baseURL = configurationProvider.getValue("AUTHENTICATION_API_BASE_URL");
          this._unauthenticatedClient = axios.create({
            baseURL
          });
        }
        return this._unauthenticatedClient;
      }
      async createToken() {
        if (this.pendingCreateTokenRequest) {
          return this.pendingCreateTokenRequest;
        }
        const storeKey = configurationProvider.getValue("STORE_KEY");
        this.pendingCreateTokenRequest = this.unauthenticatedClient.get("/createToken", {
          params: {
            storeKey
          },
          withCredentials: true
        });
        try {
          const response = await this.pendingCreateTokenRequest;
          this.pendingCreateTokenRequest = void 0;
          return response;
        } catch (error) {
          this.pendingCreateTokenRequest = void 0;
          throw error;
        }
      }
      /**
       * Logs the user in.
       * Note: you should wrap this in a try catch to handle any error scenarios.
       */
      async login(email, password, anonymousCartId) {
        const portalId = configurationProvider.getValue("PORTAL_ID");
        const storeKey = configurationProvider.getValue("STORE_KEY");
        return this.authenticatedClient.post("/login", {
          email,
          password,
          anonymousCartId,
          store: storeKey,
          portalId
        });
      }
    };
    authenticationHttpClient = new AuthenticationHttpClient();
    AuthenticationHttpClient_default = authenticationHttpClient;
  }
});

// src/services/AuthenticationService.ts
var AuthenticationService_exports = {};
__export(AuthenticationService_exports, {
  AuthenticationService: () => AuthenticationService,
  default: () => AuthenticationService_default
});
import { jwtDecode } from "jwt-decode";
var localStorageKey, AuthenticationService, authenticationService, AuthenticationService_default;
var init_AuthenticationService = __esm({
  "src/services/AuthenticationService.ts"() {
    "use strict";

    init_AuthenticationHttpClient();
    localStorageKey = "authToken";
    AuthenticationService = class {
      constructor() {
        this.tokenChangeSubscriptions = /* @__PURE__ */new Set();
        this.loginSubscriptions = /* @__PURE__ */new Set();
        this.logoutSubscriptions = /* @__PURE__ */new Set();
        this.externalTokenChangeSubscriptions = /* @__PURE__ */new Set();
        this.getTokenFromLocalStorage();
        if (typeof window !== "undefined") {
          window.addEventListener("storage", event => {
            if (event.key === localStorageKey) {
              const newToken = event.newValue || void 0;
              if (newToken !== this._token) {
                const prevToken = this._token;
                this._token = newToken;
                this.notifyTokenChangeSubscribers(newToken, prevToken);
              }
            }
          });
        }
      }
      notifyTokenChangeSubscribers(newToken, prevToken) {
        this.tokenChangeSubscriptions.forEach(subscription => {
          subscription(newToken, prevToken);
        });
        this.externalTokenChangeSubscriptions.forEach(subscription => {
          subscription(newToken, prevToken);
        });
      }
      get token() {
        return this._token;
      }
      set token(token) {
        if (this._token !== token) {
          const prevToken = this._token;
          this._token = token;
          this.setTokenInLocalStorage();
          this.notifyTokenChangeSubscribers(token, prevToken);
        }
      }
      get decodedToken() {
        return this.token ? jwtDecode(this.token) : void 0;
      }
      /**
       * Note: Do not use `isTokenAnonymous` to check if the user is logged in, as there are multiple other factors that
       * determine whether the user is logged in.
       * Instead, use: `isUserLoggedIn`
       */
      get isTokenAnonymous() {
        return this.decodedToken && typeof this.decodedToken.anonymous === "boolean" ? this.decodedToken.anonymous : false;
      }
      get isTokenExpired() {
        return this.decodedToken && typeof this.decodedToken.exp === "number" ? Boolean(this.decodedToken.exp <= Math.floor(( /* @__PURE__ */new Date()).getTime() / 1e3)) : false;
      }
      get isUserLoggedIn() {
        return Boolean(this.token) && !this.isTokenAnonymous && !this.isTokenExpired;
      }
      getTokenFromLocalStorage() {
        if (typeof window === "undefined") {
          return;
        }
        this.token = window.localStorage.getItem(localStorageKey) || void 0;
      }
      setTokenInLocalStorage() {
        if (typeof window === "undefined") {
          return;
        }
        if (this.token) {
          window.localStorage.setItem(localStorageKey, this.token);
        } else {
          window.localStorage.removeItem(localStorageKey);
        }
      }
      async createAndStoreToken() {
        const response = await AuthenticationHttpClient_default.createToken();
        this.token = response.data.token;
        return void 0;
      }
      async authenticate() {
        if (!this.token || this.isTokenExpired) {
          await this.createAndStoreToken();
        }
        return void 0;
      }
      /**
       * Logs the user in.
       * Note: you should wrap this in a try catch to handle any error scenarios.
       */
      async login(email, password, anonymousCartId) {
        const response = await AuthenticationHttpClient_default.login(email, password, anonymousCartId);
        if (!response.data.success) {
          throw new Error(response.data.reason || "An unknown error occurred while logging in.");
        }
        this.token = response.data.token;
        this.loginSubscriptions.forEach(subscription => {
          subscription(response.data);
        });
        return void 0;
      }
      logout() {
        this.token = void 0;
        this.logoutSubscriptions.forEach(subscription => {
          subscription();
        });
      }
      onTokenChange(subscription) {
        this.tokenChangeSubscriptions.add(subscription);
        return () => {
          this.tokenChangeSubscriptions.delete(subscription);
        };
      }
      onLogin(subscription) {
        this.loginSubscriptions.add(subscription);
        return () => {
          this.loginSubscriptions.delete(subscription);
        };
      }
      onLogout(subscription) {
        this.logoutSubscriptions.add(subscription);
        return () => {
          this.logoutSubscriptions.delete(subscription);
        };
      }
      /**
       * @deprecated
       * This exists temporarily and only to sync between launchpad's authSaga and this AuthenticationService.
       * Do not use this for anything else.
       * TODO: remove this method once launchpad's authSaga is phased out completely.
       */
      syncWithTokenChangeInExternalAuthenticationLayer(token) {
        if (token !== this._token) {
          const prevToken = this._token;
          this._token = token;
          this.tokenChangeSubscriptions.forEach(subscription => {
            subscription(token, prevToken);
          });
        }
      }
      /**
       * @deprecated
       * This exists temporarily and only to sync between launchpad's authSaga and this AuthenticationService.
       * Do not use this for anything else.
       * TODO: remove this method once launchpad's authSaga is phased out completely.
       */
      syncExternalAuthenticationLayerWithTokenChange(subscription) {
        this.externalTokenChangeSubscriptions.add(subscription);
        return () => {
          this.externalTokenChangeSubscriptions.delete(subscription);
        };
      }
      /**
       * @deprecated
       * This exists temporarily and only to sync between launchpad's authSaga and this AuthenticationService.
       * Do not use this for anything else.
       * TODO: remove this method once launchpad's authSaga is phased out completely.
       */
      syncWithLoginEventInExternalAuthenticationLayer(loginResponseData) {
        this.loginSubscriptions.forEach(subscription => {
          subscription(loginResponseData);
        });
      }
      /**
       * @deprecated
       * This exists temporarily and only to sync between launchpad's authSaga and this AuthenticationService.
       * Do not use this for anything else.
       * TODO: remove this method once launchpad's authSaga is phased out completely.
       */
      syncWithLogoutEventInExternalAuthenticationLayer() {
        this.logoutSubscriptions.forEach(subscription => {
          subscription();
        });
      }
    };
    authenticationService = new AuthenticationService();
    AuthenticationService_default = authenticationService;
  }
});

// src/http-client-interceptors/authenticateHttpRequestInterceptor.ts
async function authenticateHttpRequestInterceptor(config) {
  const authenticationService2 = (init_AuthenticationService(), __toCommonJS(AuthenticationService_exports)).default;
  await authenticationService2.authenticate();
  if (!authenticationService2.token) {
    throw new Error("Could not retrieve a token to authenticate the HTTP request.");
  }
  const updatedConfig = config;
  updatedConfig.headers.Authorization = authenticationService2.token;
  return updatedConfig;
}
var init_authenticateHttpRequestInterceptor = __esm({
  "src/http-client-interceptors/authenticateHttpRequestInterceptor.ts"() {
    "use strict";
  }
});
export { authenticateHttpRequestInterceptor, init_authenticateHttpRequestInterceptor, AuthenticationHttpClient_default, init_AuthenticationHttpClient, AuthenticationService_default, init_AuthenticationService };